import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { GraduationCap, TrendingUp, AlertCircle, Ban, RotateCcw, ChevronDown, ChevronUp, Search, Trophy, BookOpen } from "lucide-react";
import { handleGet, handlePost } from "../../../controllers/controller.all";

const APPRECIATION_COLOR = {
  "Excellent": "text-emerald-700 bg-emerald-50",
  "Très Bien": "text-teal-700 bg-teal-50",
  "Bien": "text-blue-700 bg-blue-50",
  "Assez Bien": "text-indigo-700 bg-indigo-50",
  "Passable": "text-amber-700 bg-amber-50",
  "Insuffisant": "text-red-700 bg-red-50",
};

function MoyenneBadge({ value }) {
  const n = parseFloat(value) || 0;
  const color = n >= 16 ? "text-emerald-700 bg-emerald-100" : n >= 12 ? "text-blue-700 bg-blue-100"
    : n >= 10 ? "text-amber-700 bg-amber-100" : "text-red-700 bg-red-100";
  return <span className={`text-sm font-bold px-2.5 py-1 rounded-lg ${color}`}>{n.toFixed(2)}/20</span>;
}

function StudentCard({ bulletin, expanded, onToggle }) {
  const st = bulletin.student || {};
  const name = `${st.prenom || ""} ${st.nom || ""}`.trim();
  const absColor = bulletin.absences > 5 ? "text-red-600" : bulletin.absences > 2 ? "text-amber-600" : "text-gray-500";

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-gray-50 transition">
        {/* Rank */}
        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold
          ${bulletin.rang === 1 ? "bg-amber-100 text-amber-700" : bulletin.rang === 2 ? "bg-gray-200 text-gray-600" : bulletin.rang === 3 ? "bg-orange-100 text-orange-700" : "bg-indigo-50 text-indigo-600"}`}>
          {bulletin.rang <= 3 ? ["🥇","🥈","🥉"][bulletin.rang-1] : `#${bulletin.rang}`}
        </div>
        {/* Name */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">{name}</div>
          <div className="text-xs text-gray-400">{st.numero_etudiant || "—"}</div>
        </div>
        {/* Stats */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <MoyenneBadge value={bulletin.moyenne_generale} />
          <span className={`text-xs ${absColor}`}>{bulletin.absences} abs.</span>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4">
          {/* Appreciation */}
          <div className={`inline-block text-xs px-2.5 py-1 rounded-full font-medium mb-4 ${APPRECIATION_COLOR[bulletin.appreciation_generale] || "text-gray-600 bg-gray-100"}`}>
            {bulletin.appreciation_generale}
          </div>
          {/* Subject table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 text-left text-gray-500 font-medium">Matière</th>
                  <th className="pb-2 text-center text-gray-500 font-medium">Coef.</th>
                  <th className="pb-2 text-center text-gray-500 font-medium">Moyenne</th>
                  <th className="pb-2 text-left text-gray-500 font-medium">Appréciation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(bulletin.matieres || []).map((m, i) => {
                  const matName = typeof m.matiere === "object" ? m.matiere?.libelle : m.matiere;
                  const avgColor = parseFloat(m.moyenne) >= 10 ? "text-emerald-600" : "text-red-500";
                  return (
                    <tr key={i}>
                      <td className="py-2 text-gray-700 font-medium">{matName}</td>
                      <td className="py-2 text-center text-gray-500">{m.coef}</td>
                      <td className={`py-2 text-center font-bold ${avgColor}`}>{parseFloat(m.moyenne).toFixed(2)}</td>
                      <td className="py-2 text-gray-500">{m.appreciation}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BulletinView() {
  const user = useSelector(s => s.userReducer?.user);
  const token = user?.access_token || "";
  const ecole = user?.ecole;
  const ecoleId = ecole?._id || ecole || "";

  const [classes, setClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState("");
  const [selectedPeriode, setSelectedPeriode] = useState("");
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [excludePanel, setExcludePanel] = useState(false);
  const [excludeDates, setExcludeDates] = useState({ debut: "", fin: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token || !ecoleId) return;
    handleGet(`/classe?ecole=${ecoleId}`, res => setClasses(res?.classes || []), token);
    handleGet(`/period?ecole=${ecoleId}&limit=100000`, res => setPeriods(res?.periods || []), token);
  }, [token, ecoleId]);

  const fetchBulletins = useCallback(async () => {
    if (!selectedClasse || !selectedPeriode) return;
    setLoading(true);
    await handleGet(`/bulletin/classe/${selectedClasse}/periode/${selectedPeriode}`, res => {
      setBulletins(res?.bulletins || []);
    }, token);
    setLoading(false);
  }, [selectedClasse, selectedPeriode, token]);

  const handleExclude = async (exclude) => {
    if (!excludeDates.debut && !excludeDates.fin) return;
    setSaving(true);
    await handlePost("/bulletin/notes/exclude", {
      ecole_id: ecoleId,
      date_debut: excludeDates.debut,
      date_fin: excludeDates.fin,
      exclude,
    }, res => {
      if (res?.state === "success") {
        alert(res.message);
        fetchBulletins();
      }
    }, token);
    setSaving(false);
  };

  const filtered = bulletins.filter(b => {
    const name = `${b.student?.prenom || ""} ${b.student?.nom || ""}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const avg = bulletins.length ? (bulletins.reduce((s, b) => s + (b.moyenne_generale || 0), 0) / bulletins.length).toFixed(2) : "—";

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-indigo-600" /> Bulletins Scolaires
          </h1>
          <p className="text-sm text-gray-500 mt-1">Consultez et gérez les moyennes par période.</p>
        </div>
        <button onClick={() => setExcludePanel(e => !e)} className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">
          <Ban className="h-4 w-4 text-red-500" /> Exclure des notes
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col sm:flex-row gap-3">
        <select value={selectedClasse} onChange={e => setSelectedClasse(e.target.value)}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none">
          <option value="">-- Sélectionner une classe --</option>
          {classes.map(c => <option key={c._id} value={c._id}>{c.libelle}</option>)}
        </select>
        <select value={selectedPeriode} onChange={e => setSelectedPeriode(e.target.value)}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none">
          <option value="">-- Sélectionner une période --</option>
          {periods.map(p => <option key={p._id} value={p._id}>{p.libelle}</option>)}
        </select>
        <button onClick={fetchBulletins} disabled={!selectedClasse || !selectedPeriode || loading}
          className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
          {loading ? "Chargement..." : "Calculer"}
        </button>
      </div>

      {/* Exclusion panel */}
      {excludePanel && (
        <div className="bg-red-50 rounded-xl border border-red-200 p-4 flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" /> Exclure les notes par date de devoir
            </p>
            <div className="flex gap-2">
              <input type="date" value={excludeDates.debut} onChange={e => setExcludeDates(d => ({ ...d, debut: e.target.value }))}
                className="flex-1 border border-red-200 rounded-lg px-3 py-2 text-sm" />
              <input type="date" value={excludeDates.fin} onChange={e => setExcludeDates(d => ({ ...d, fin: e.target.value }))}
                className="flex-1 border border-red-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => handleExclude(true)} disabled={saving}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 transition">
              <Ban className="h-4 w-4 inline mr-1" />Exclure
            </button>
            <button onClick={() => handleExclude(false)} disabled={saving}
              className="px-4 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 disabled:opacity-50 transition">
              <RotateCcw className="h-4 w-4 inline mr-1" />Réintégrer
            </button>
          </div>
        </div>
      )}

      {/* Stats cards */}
      {bulletins.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Trophy, label: "Meilleure moyenne", value: bulletins[0]?.moyenne_generale?.toFixed(2) + "/20", color: "text-amber-600 bg-amber-50" },
            { icon: TrendingUp, label: "Moyenne de la classe", value: avg + "/20", color: "text-indigo-600 bg-indigo-50" },
            { icon: BookOpen, label: "Élèves", value: bulletins.length, color: "text-emerald-600 bg-emerald-50" },
            { icon: GraduationCap, label: "Réussite (≥10)", value: bulletins.filter(b => b.moyenne_generale >= 10).length, color: "text-blue-600 bg-blue-50" },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      {bulletins.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un élève..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
        </div>
      )}

      {/* Bulletin list */}
      {loading ? (
        <div className="flex items-center justify-center h-32 text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : filtered.length === 0 && bulletins.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Sélectionnez une classe et une période pour voir les bulletins.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map(b => (
            <StudentCard key={b.student?._id} bulletin={b}
              expanded={!!expanded[b.student?._id]}
              onToggle={() => setExpanded(e => ({ ...e, [b.student?._id]: !e[b.student?._id] }))} />
          ))}
        </div>
      )}
    </div>
  );
}
